import MoreOptions from "@assets/more-options.svg";
import { IconButton, PrimaryButton, SecondaryButton } from "../bits/buttons";
import { css } from "@emotion/react";
import { useClick, useFloating, useInteractions } from "@floating-ui/react";
import { useState } from "react";
import InventoryIcon from "@mui/icons-material/Inventory";
import { IconButton as MuiIconButton } from "@mui/material";
import { Modal } from "../bits/Modal";
import { trpc } from "../../utils/trpc";
import type { Publication } from "@rowan/prisma";

export const PublicationMoreOptions = ({
  publication,
  archiveView = false,
}: {
  publication: Publication;
  archiveView?: boolean;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const archivePublication = trpc.archivePublication.useMutation({
    onSuccess: () => {
      setIsModalOpen(false);
      setIsOpen(false);
    },
  });

  const unarchivePublication = trpc.unarchivePublication.useMutation({
    onSuccess: () => {
      setIsModalOpen(false);
      setIsOpen(false);
    },
  });

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  const click = useClick(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click]);

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <Modal
        key="archiveModal"
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        renderButton={() => (
          <>
            <IconButton
              ref={refs.setReference}
              {...getReferenceProps()}
              css={css`
                border-width: 0;
                box-shadow: none;
                background-color: transparent;
                position: absolute;
                bottom: -30px;
                right: -5px;
              `}
              src={MoreOptions}
              alt="Options"
            />
            {isOpen && (
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                css={css`
                  display: flex;
                  align-items: center;
                  box-shadow:
                    0px 2px 4px rgba(9, 9, 11, 0.08),
                    0px 0px 4px rgba(224, 224, 230, 1),
                    0px 0px 2px 1px rgba(255, 255, 255, 1),
                    0px 0px 0px 0.25px rgba(234, 234, 238, 1);
                  opacity: 1;
                  background-color: rgba(244, 244, 246, 1);
                  height: 35px;
                  border-radius: 8px;
                  padding: 10px;
                `}
                {...getFloatingProps()}
              >
                <MuiIconButton
                  onClick={() => {
                    setIsModalOpen(true);
                    setIsOpen(false);
                  }}
                  title={
                    archiveView
                      ? "Unarchive Publication"
                      : "Archive Publication"
                  }
                  size="small"
                  sx={{
                    width: "24px",
                    height: "24px",
                    padding: "0",
                    borderRadius: "6px",
                  }}
                >
                  <InventoryIcon sx={{ height: "16px" }} />
                </MuiIconButton>
              </div>
            )}
          </>
        )}
        header={
          <h2>
            {archiveView ? "Unarchive Publication" : "Archive Publication"}
          </h2>
        }
        content={
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            {archiveView
              ? "Are you sure you want to unarchive this publication?"
              : "Are you sure you want to archive this publication? This will remove its content from your search results."}

            <div
              css={css`
                display: flex;
                justify-content: flex-end;
                gap: 8px;
              `}
            >
              <PrimaryButton
                onClick={() => {
                  if (archiveView) {
                    unarchivePublication.mutate({
                      publicationId: publication.id,
                    });
                  } else {
                    archivePublication.mutate({
                      publicationId: publication.id,
                    });
                  }
                }}
              >
                {archiveView ? "Unarchive" : "Archive"}
              </PrimaryButton>
              <SecondaryButton
                onClick={() => {
                  setIsModalOpen(false);
                }}
              >
                Cancel
              </SecondaryButton>
            </div>
          </div>
        }
      />
    </div>
  );
};
